import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Replay as ReplayIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import useStore from '../config/store';
import CvFilteringResults from './CvFilteringResults';

const CvFilteringDashboard = () => {
  const { t } = useTranslation();
  const { authToken, project, user, showAlert } = useStore();

  const [loading, setLoading] = useState(false);
  const [filterings, setFilterings] = useState([]);
  const [selectedFiltering, setSelectedFiltering] = useState(null);
  const [processing, setProcessing] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [filteringToDelete, setFilteringToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (project) loadFilterings();
  }, [project]);

  const loadFilterings = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/cv-filterings?projectId=${project}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode === 0) {
        setFilterings(response.data.response.data || []);
      }
    } catch (err) {
      console.error('Error loading filterings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filterings || filterings.length === 0) return;

    const unsubscribers = [];

    filterings.forEach((filtering) => {
      if (filtering.status === 'processing') {
        const filteringRef = doc(db, 'cvFilterings', filtering.id);

        const unsubscribe = onSnapshot(filteringRef, (docSnap) => {
          if (!docSnap.exists()) return;

          const data = docSnap.data();

          if (data.status && data.status !== 'processing') {
            console.log(
              `✅ Filtering ${filtering.id} cambió de processing a ${data.status}`
            );
            loadFilterings();
          }
        });

        unsubscribers.push(unsubscribe);
      }
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [filterings]);

  const handleReprocess = async (filteringId, status) => {
    setProcessing((prev) => ({ ...prev, [filteringId]: true }));

    try {
      const endpoint = status === 'completed'
        ? `/cv-filterings/${filteringId}/reprocess`
        : `/cv-filterings/${filteringId}/process`;

      const response = await api.post(
        endpoint,
        { language: 'es' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode !== 0) {
        throw new Error('Error al iniciar reprocesamiento');
      }

      await loadFilterings();
    } catch (err) {
      console.error('Error reprocessing:', err);
      showAlert(t('cvFiltering.reprocessError'), 'error');
    } finally {
      setProcessing((prev) => ({ ...prev, [filteringId]: false }));
    }
  };

  const handleDelete = (filtering) => {
    setFilteringToDelete(filtering);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!filteringToDelete) return;

    try {
      setDeleting(true);
      const response = await api.delete(
        `/cv-filterings/${filteringToDelete.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode === 0) {
        showAlert(t('cvFiltering.deleteSuccess'), 'success');
        await loadFilterings();
      } else {
        showAlert(t('cvFiltering.deleteError'), 'error');
      }
    } catch (err) {
      console.error('Error deleting filtering:', err);
      showAlert(t('cvFiltering.deleteError'), 'error');
    } finally {
      setDeleting(false);
      setOpenDeleteDialog(false);
      setFilteringToDelete(null);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setFilteringToDelete(null);
  };

  const handleViewResult = (filtering) => {
    setSelectedFiltering(filtering);
  };

  const handleBackToDashboard = () => {
    setSelectedFiltering(null);
    loadFilterings();
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { label: t('cvFiltering.statusPending'), color: 'default' },
      processing: { label: t('cvFiltering.statusProcessing'), color: 'warning' },
      completed: { label: t('cvFiltering.statusCompleted'), color: 'success' },
      failed: { label: t('cvFiltering.statusFailed'), color: 'error' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';

    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp._seconds) {
      date = new Date(timestamp._seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (selectedFiltering) {
    return (
      <CvFilteringResults
        filtering={selectedFiltering}
        onBack={handleBackToDashboard}
      />
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t('cvFiltering.dashboardTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('cvFiltering.dashboardSubtitle')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filterings.length === 0 ? (
        <Alert severity="info">
          {t('cvFiltering.noProcesses')}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('cvFiltering.tableTitle')}</TableCell>
                <TableCell>{t('cvFiltering.tablePosition')}</TableCell>
                <TableCell>{t('cvFiltering.tableApplicants')}</TableCell>
                <TableCell>{t('cvFiltering.tableStatus')}</TableCell>
                <TableCell>{t('cvFiltering.tableDate')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filterings.map((filtering) => (
                <TableRow key={filtering.id}>
                  <TableCell>{filtering.title}</TableCell>
                  <TableCell>{filtering.positionName}</TableCell>
                  <TableCell>{filtering.applicants?.length || 0}</TableCell>
                  <TableCell>{getStatusChip(filtering.status)}</TableCell>
                  <TableCell>{formatDate(filtering.created)}</TableCell>

                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>

                      {/* VER - Mostrar si está processing o completed */}
                      {(filtering.status === 'processing' ||
                        filtering.status === 'completed') && (
                          <Tooltip
                            title={
                              filtering.status === 'processing'
                                ? t('cvFiltering.processing')
                                : t('cvFiltering.viewResults')
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleViewResult(filtering)}
                                disabled={filtering.status === 'processing'}
                                color="primary"
                              >
                                {filtering.status === 'processing' ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <AssessmentIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                      {/* REPROCESAR - Solo completed y super-admin */}
                      {filtering.status === 'completed' &&
                        user?.rol === 'super-admin' && (
                          <Tooltip title={t('cvFiltering.reprocess')}>
                            <IconButton
                              size="small"
                              onClick={() => handleReprocess(filtering.id, filtering.status)}
                              disabled={!!processing[filtering.id]}
                              color="warning"
                            >
                              {!!processing[filtering.id] ? (
                                <CircularProgress size={20} />
                              ) : (
                                <ReplayIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}

                      {/* REINTENTAR - Solo failed y super-admin */}
                      {filtering.status === 'failed' && (
                          <Tooltip title={t('cvFiltering.retry')}>
                            <IconButton
                              size="small"
                              onClick={() => handleReprocess(filtering.id, filtering.status)}
                              disabled={!!processing[filtering.id]}
                              color="warning"
                            >
                              {!!processing[filtering.id] ? (
                                <CircularProgress size={20} />
                              ) : (
                                <ReplayIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}

                      {/* ELIMINAR - Solo super-admin */}
                      {user?.rol === 'super-admin' && (
                        <Tooltip title={t('delete')}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(filtering)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog de confirmación para eliminar */}
      <Dialog open={openDeleteDialog} onClose={cancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>{t('cvFiltering.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('cvFiltering.deleteConfirm')} <strong>"{filteringToDelete?.title}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('noWayBack')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={deleting}>
            {t('cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? t('deleting') : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CvFilteringDashboard;